<template>
  <button
    :class="[
      'flex items-center font-medium transition-colors duration-200',
      variantClass,
      sizeClass,
      light ? 'hover:bg-opacity-20' : '',
      disabled || loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      block ? 'w-full' : '',
    ]"
    :disabled="disabled || loading"
    :type="props.type"
    @click.stop="handleClick($event)"
  >
    <div v-if="loading" class="flex items-center w-full justify-center">
      <b-loader-spinner class="mr-2" />
      {{ loadingText || t('common.loading') }}
    </div>
    <div v-else class="flex items-center justify-center w-full">
      <Icon v-if="prependIcon" :name="prependIcon" :size="iconSize" class="mr-1.5" />
      <span :class="['flex justify-center w-full h-full font-medium', { 'text-center': props.center }]">
        <slot></slot>
      </span>
      <Icon v-if="appendIcon" :name="appendIcon" class="ml-1.5" :size="iconSize" />
    </div>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import BLoaderSpinner from "~/components/loader/BLoaderSpinner.vue";

const { t } = useI18n();

interface Props {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'destructive' | 'filled';
  light?: boolean;
  loading?: boolean;
  outline?: boolean;
  disabled?: boolean;
  center?: boolean;
  size?: string;
  type?: 'button' | 'submit' | 'reset';
  prependIcon?: string;
  appendIcon?: string;
  loadingText?: string;
  block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'lg',
  light: false,
  loading: false,
  disabled: false,
  prependIcon: '',
  appendIcon: '',
  type: "button"
});

const emit = defineEmits(['click']);
const clicked = ref(false);

const handleClick = (event: MouseEvent) => {
  if (clicked.value) {
    return;
  }
  clicked.value = true;
  emit('click', event);
  setTimeout(() => {
    clicked.value = false;
  }, 300); // Debounce for 300ms
};

const variantClass = computed(() => {
  switch (props.variant) {
    case 'primary':
      return props.light ? 'text-white hover:bg-gray-600 bg-white/10' : 'bg-white text-gray-900 hover:bg-gray-200';
    case 'tertiary':
      return 'bg-[#3c3c3c] text-[#f2f2f2] hover:bg-[#4a4a4a]';
    case 'success':
      return props.light ? 'text-green-600 hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700';
    case 'secondary':
      return props.light ? 'text-primary bg-primary bg-opacity-10 hover:bg-primary hover:bg-opacity-20' : 'bg-green-600 text-white hover:bg-green-700';
    case 'destructive':
      return props.light ? 'text-red-500 hover:bg-red-700 bg-red-600 bg-opacity-20' : 'bg-red-600 text-white hover:bg-red-700';
    default:
      return '';
  }
});

const sizeClass = computed(() => {
  switch (props.size) {
    case 'lg':
      return 'h-[50px] px-3 rounded-lg text-md';
    case 'sm':
      return 'h-[32px] px-3 text-sm rounded-md';
    case 'xs':
      return 'h-[24px] px-1.5 rounded-md text-[10px]';
    default:
      return 'h-[50px] px-3 rounded-lg text-md';
  }
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'lg':
      return '1.5rem';
    case 'sm':
      return '1rem';
    case 'xs':
      return '0.75rem';
    default:
      return '1.5rem';
  }
});
</script>
